const DEFAULT_MAX_WINDOW_HOPS = 10;

/**
 * True when `target` is a defined, same-origin window: its `document` is
 * readable (a cross-origin access throws and is treated as not-accessible).
 */
export function isSameOriginWindow(target: unknown): boolean {
  try {
    return (
      target !== undefined &&
      target !== null &&
      (target as { document?: unknown }).document !== undefined
    );
  } catch (_) {
    return false;
  }
}

export function canAccessParentWindow(aWindow: Window): boolean {
  try {
    // Reachable + same-origin AND not the window itself: the top window's
    // `parent` points back to itself, so it has no real parent to access.
    return isSameOriginWindow(aWindow.parent) && aWindow.parent !== aWindow;
  } catch (_) {
    return false;
  }
}

export function getParentWindow(
  aWindow: Window | undefined
): Window | undefined {
  if (!aWindow) {
    return undefined;
  }
  if (!canAccessParentWindow(aWindow)) {
    return undefined;
  }

  return aWindow.parent;
}

export function findInWindowAncestors<T>(
  startWindow: Window | undefined,
  readValue: (candidate: Window) => T | undefined,
  maxHops: number = DEFAULT_MAX_WINDOW_HOPS
): T | undefined {
  let current = startWindow;

  for (let hops = 0; hops < maxHops && current; hops++) {
    const value = readValue(current);
    if (value !== undefined) {
      return value;
    }

    current = getParentWindow(current);
  }

  return undefined;
}
